import { type FC, useEffect, useRef } from 'react';
import { Box, Flex, Image } from '@chakra-ui/react';
import { getAssetUrl } from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage, useIntl } from 'react-intl';
import 'swiper/css';
import { useBuildURL } from '../../../hooks/useBuildURL';
import { useNavigateTo } from '../../../hooks/useNavigateTo';
import { useRecommendations } from '../../../hooks/useRecommendations';
import { Header, Text } from '../../../ui-components';
import { MenuSearch } from '../../header/menu/menu-search';
import { RecommendationLocation } from '../../recommendations/RecommendationLocation';
import { EnhancedErrorCategories } from '../EnhancedErrorCategories';

export const EnhancedError: FC<{
    error?: { status?: number; message?: string; body?: unknown; stack?: string };
}> = () => {
    const intl = useIntl();
    const recommendationWidgets = useRecommendations({
        location: RecommendationLocation.PDP_ERROR,
    });
    const headerRef = useRef<HTMLDivElement>(null);
    const navigateTo = useNavigateTo();
    const { buildSearchProductURL } = useBuildURL();
    useEffect(() => {
        const handleSearchEvent = (event: CustomEvent<string>): void => {
            navigateTo(buildSearchProductURL(event.detail), { useWindowLocation: true });
        };

        window.addEventListener('slt-search', handleSearchEvent as EventListener);
        return () => {
            window.removeEventListener('slt-search', handleSearchEvent as EventListener);
        };
    }, [buildSearchProductURL, navigateTo]);

    return (
        <>
            <Flex
                data-testid="oops-error-container"
                background="white"
                direction="column"
                alignItems="center"
                marginX={{ base: 0, sm: 8, lg: 10, xl: 16 }}
                pt={8}
            >
                <Flex
                    gap={{ base: 8, sm: 'unset' }}
                    width="100%"
                    maxWidth={1792}
                    direction={{ base: 'column', sm: 'row' }}
                    alignItems={{ sm: 'center', md: 'unset' }}
                >
                    <Flex
                        flex={{ base: 0, sm: 1 }}
                        direction="column"
                        justifyContent="center"
                        gap={8}
                        marginX={{ base: 4, sm: 'unset' }}
                    >
                        <Flex direction="column">
                            <Header
                                id="oops-error-title"
                                margin="0px 0px 4px"
                                as="h3"
                                variant="header3"
                                style="bold"
                            >
                                <FormattedMessage
                                    id="error.generic.title"
                                    defaultMessage="OOPS. Well, that page didn’t turn out."
                                />
                            </Header>
                            <Text id="oops-error-subtitle" type="body">
                                <FormattedMessage
                                    id="error.generic.main-message"
                                    defaultMessage="Don't worry, even the best chefs get lost sometimes."
                                />
                            </Text>
                        </Flex>

                        <Text id="oops-error-call-to-action" type="body" style="bold">
                            <FormattedMessage
                                id="error.generic.call-to-action"
                                defaultMessage="We'll check our recipe. Meanwhile, try entering what you are looking for in the search bar below."
                            />
                        </Text>
                        <ErrorBoundary fallback={null}>
                            <Box width={{ base: '100%', md: '480px' }}>
                                <MenuSearch
                                    headerRef={headerRef}
                                    forceReloadOnSearch
                                    placeholder={intl.formatMessage({
                                        id: 'error.search.placeholder',
                                        defaultMessage: 'Try a new search',
                                    })}
                                    showSuggestions={false}
                                />
                            </Box>
                        </ErrorBoundary>
                    </Flex>
                    <Flex
                        flex={{ base: 0, sm: 1 }}
                        direction={{ base: 'column', md: 'row' }}
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Image
                            data-testid="oops-error-image"
                            src={getAssetUrl('static/img/pdp/error-image.png')}
                            alt="Error image"
                            width={{ base: '375px', lg: 'auto' }}
                            height={{ base: 'auto', lg: '582.4px', xl: '672px' }}
                        />
                    </Flex>
                </Flex>
            </Flex>
            <ErrorBoundary fallback={null}>
                <Box mt={8} marginX={{ base: 0, sm: 8, lg: 10, xl: 16 }}>
                    {recommendationWidgets}
                </Box>
            </ErrorBoundary>
            <ErrorBoundary fallback={null}>
                <Box mt={8}>
                    <EnhancedErrorCategories />
                </Box>
            </ErrorBoundary>
        </>
    );
};
